import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'clsx';
import get from 'lodash/get';

import ClampedText from 'react-dotdotdot';
import DisplayAd from 'src/common/components/ads/DisplayAd';
import { isMedium, isXLarge, isXXLarge } from 'src/common/utils/breakpoints';
import { toggleProfileFavorite } from '../../actions/favorites';
import { sharingFromProfile } from '../../actions/share';
import { wrapProtectedActions } from '../../actions/auth';
import Banner from '../shared/Banner';
import isFollowingGuideId from '../../utils/isFollowingGuideId';
import SocialControls from './SocialControls';
import PageTitles from './PageTitles';
import TeamLogos from './TeamLogos';
import ProfileLogo from './ProfileLogo';

import css from './header.module.scss';

export function Header({
  guideItem,
  actions,
  profileActions,
  bannerImage,
  image,
  title,
  description,
  affiliate,
  genres,
  subtitle,
  teamsInfo,
  breakpoint,
  routeProps,
  isFollowing,
  canShowAds,
}) {
  const shouldShowSocialControls =
    profileActions.follow && profileActions.share;
  const canShowDisplayAd =
    canShowAds &&
    ((isMedium(breakpoint) && !isXLarge(breakpoint)) || isXXLarge(breakpoint));

  return (
    <div>
      {bannerImage ? (
        <Banner src={bannerImage} title={title} />
      ) : (
        canShowDisplayAd && (
          <DisplayAd
            id="profile_top"
            slotName="profile_top"
            style={{ marginBottom: '45px' }}
            guideId={get(routeProps, 'guideContext.guideId')}
            matchUrl={routeProps.matchUrl}
            isProfile
          />
        )
      )}

      <section className={classNames('row', css.section)}>
        <div
          className={classNames(
            'col-md-10',
            'col-sm-9',
            'col-xs-12',
            css.logoTitleContainer,
          )}
        >
          {teamsInfo ? (
            <TeamLogos teamsInfo={teamsInfo} />
          ) : (
            <ProfileLogo key={image} image={image} title={title} />
          )}
          <PageTitles
            className={classNames(css.titleContainer, {
              [css.isTeamTitle]: teamsInfo,
              [css.hasDescription]: !!description,
            })}
            title={title}
            subtitle={subtitle}
            description={description}
            affiliate={affiliate}
            genres={genres}
            isGame={!!teamsInfo}
          />
        </div>
        {description && (
          <ClampedText
            data-testid="profileHeaderDescription"
            className={css.description}
            clamp={4}
          >
            {description}
          </ClampedText>
        )}
        <div className="col-md-2 col-sm-3 col-xs-12">
          {shouldShowSocialControls && (
            <SocialControls
              canFollow={get(profileActions, 'follow.canFollow')}
              isFollowing={isFollowing}
              guideItem={guideItem}
              handleFavoriteToggle={() =>
                actions.toggleProfileFavorite({
                  guideItem,
                })
              }
              handleSharing={() => {
                actions.sharingFromProfile(guideItem);
              }}
            />
          )}
        </div>
      </section>
    </div>
  );
}

Header.propTypes = {
  breakpoint: PropTypes.number.isRequired,
  routeProps: PropTypes.object.isRequired,
  canShowAds: PropTypes.bool.isRequired,
  // from mapDispatchToProps
  actions: PropTypes.object.isRequired,
  // from mapStateToProps
  guideItem: PropTypes.object.isRequired,
  isFollowing: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  profileActions: PropTypes.object.isRequired,
  subtitle: PropTypes.string,
  bannerImage: PropTypes.string,
  teamsInfo: PropTypes.object,
  description: PropTypes.string,
  affiliate: PropTypes.object,
  genres: PropTypes.object,
};

Header.defaultProps = {
  profileActions: {},
  actions: {},
  image: '',
  title: '',
};

function mapStateToProps(state, ownProps) {
  const { selectedGuideId } = ownProps;
  const { profiles } = state;
  const profile = get(profiles, [selectedGuideId], {});
  const {
    actions,
    bannerImage,
    image,
    title,
    subtitle,
    properties,
    presentation,
  } = profile;

  const teamsInfo = get(properties, 'teamsInfo');
  const description =
    get(presentation, 'expandableDescription.canExpand') &&
    get(properties, 'description.text');
  const affiliate = get(properties, 'affiliate');
  const genres = get(properties, 'genres');

  const initialFollowState = get(actions, 'follow.isFollowing', false);
  const isFollowing = isFollowingGuideId(
    state,
    selectedGuideId,
    initialFollowState,
  );

  return {
    guideItem: profile,
    profileActions: actions,
    bannerImage,
    image,
    title,
    subtitle,
    description,
    affiliate,
    genres,
    teamsInfo,
    isFollowing,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...wrapProtectedActions({ toggleProfileFavorite }),
        sharingFromProfile,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Header);
